import React from "react";
import { toast } from "react-toastify";
import {
  MAX_DIAGRAM_IMAGE_BYTES,
  fileToDataUrl,
  normalizeImageSource,
  normalizeDiagramImages,
} from "./sf009DiagramImageUtils";

const THEME = "#8b1634";
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";

export default function Sf009DiagramUploadRow({
  diagramImages: diagramImagesProp,
  inputResetKey,
  onInputResetKeyChange,
  onImagesChange,
  onPreview,
  valCellStyle,
}) {
  const diagramImages = normalizeDiagramImages(diagramImagesProp);

  const handleDiagramSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (!imageFiles.length) {
      toast.error("Please select image files.");
      onInputResetKeyChange((k) => k + 1);
      return;
    }
    const oversized = imageFiles.find((f) => f.size > MAX_DIAGRAM_IMAGE_BYTES);
    if (oversized) {
      toast.error("Each image must be 5 MB or smaller.");
      onInputResetKeyChange((k) => k + 1);
      return;
    }
    try {
      const dataUrls = await Promise.all(imageFiles.map((file) => fileToDataUrl(file)));
      const valid = dataUrls.filter(Boolean);
      if (!valid.length) {
        toast.error("Failed to load images.");
        return;
      }
      onImagesChange([...diagramImages, ...valid]);
    } catch {
      toast.error("Failed to load images.");
    } finally {
      onInputResetKeyChange((k) => k + 1);
    }
  };

  return (
    <tr>
      <td colSpan={6} style={{ ...valCellStyle, padding: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 12, color: THEME }}>Attach diagram / images</span>
          <span
            style={{
              background: THEME_LIGHT,
              color: "#334155",
              border: `1px solid ${THEME_BORDER}`,
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {diagramImages.length} image{diagramImages.length === 1 ? "" : "s"}
          </span>
        </div>
        <input
          key={inputResetKey}
          type="file"
          accept="image/*"
          multiple
          onChange={handleDiagramSelect}
          className="form-control form-control-sm"
          style={{ borderColor: THEME_BORDER, borderRadius: 8, marginBottom: 10, fontSize: 12 }}
        />
        <div
          style={{
            border: `1px dashed ${THEME_BORDER}`,
            borderRadius: 8,
            minHeight: 100,
            background: "#fafbfc",
            padding: 10,
          }}
        >
          {diagramImages.length ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10 }}>
              {diagramImages.map((img, idx) => (
                <div
                  key={`${idx}-${String(img).slice(0, 24)}`}
                  style={{
                    position: "relative",
                    border: `1px solid ${THEME_BORDER}`,
                    borderRadius: 8,
                    background: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={normalizeImageSource(img)}
                    alt={`Diagram ${idx + 1}`}
                    title="Click to preview"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      onPreview({
                        src: normalizeImageSource(img),
                        title: `Diagram ${idx + 1}`,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPreview({
                          src: normalizeImageSource(img),
                          title: `Diagram ${idx + 1}`,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      height: 110,
                      objectFit: "contain",
                      display: "block",
                      background: "#f8fafc",
                      cursor: "pointer",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "5px 8px",
                      borderTop: `1px solid ${THEME_BORDER}`,
                    }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>Image {idx + 1}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onImagesChange(diagramImages.filter((_, i) => i !== idx));
                      }}
                      style={{
                        padding: "2px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                        border: "1px solid #dc2626",
                        borderRadius: 6,
                        background: "#fff",
                        color: "#dc2626",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                minHeight: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
                gap: 4,
                fontSize: 12,
              }}
            >
              <span>No diagram uploaded yet</span>
            </div>
          )}
        </div>
        {diagramImages.length > 0 && (
          <button
            type="button"
            onClick={() => onImagesChange([])}
            style={{
              marginTop: 8,
              padding: "4px 12px",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid #dc2626",
              borderRadius: 6,
              background: "#fff",
              color: "#dc2626",
              cursor: "pointer",
            }}
          >
            Remove all images
          </button>
        )}
      </td>
    </tr>
  );
}
